import { useState, useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, Modal, Tab, Tabs, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import withLocalization from '../../../../hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import Button from '~/components/CustomButton/CustomButton.jsx';
import SwitchWidget from '../SwitchWidget';
import TableWidget from '../TableWidget';
import TablePagination from '~/components/GenericList/TablePagination';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';
import { Input } from '~/elements/Input';

//assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

//utils
import { NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS } from '../../../../utils/constants';
import { formatKey } from '../../../../utils/commonMethods';

const DEFAULT_FORM = { template_name: '', is_default: false, settings: {} };
const PAGE_SIZE = 10;

const NotificationTemplates = ({ t, notificationStore, commonStore, handleDelete }) => {
    const { templates, templatesPages, masterNotifications, loading } = notificationStore;
    const [isEdit, setIsEdit] = useState(false);
    const [editData, setEditData] = useState({});
    const [form, setForm] = useState({ ...DEFAULT_FORM });
    const [saving, setSaving] = useState(false);
    const [activeCategory, setActiveCategory] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [templateSearch, setTemplateSearch] = useState('');
    const [defaultOnly, setDefaultOnly] = useState(false);
    const initialFormRef = useRef({ ...DEFAULT_FORM });

    const fetchTemplates = (targetPage = page) => notificationStore.loadTemplates({
        page: targetPage,
        pagesize: PAGE_SIZE,
        ...(templateSearch.trim() && { search: templateSearch.trim() }),
        ...(defaultOnly && { is_default: true }),
    });

    useEffect(() => {
        fetchTemplates(0);
        notificationStore.loadMasterNotifications(true);
    }, []);

    const skipNextSearch = useRef(true);
    useEffect(() => {
        if (skipNextSearch.current) {
            skipNextSearch.current = false;
            return;
        }
        const timeout = setTimeout(() => {
            setPage(0);
            fetchTemplates(0);
        }, 400);
        return () => clearTimeout(timeout);
    }, [templateSearch, defaultOnly]);

    const buildDefaultSettings = () => {
        const settings = {};
        Object.values(masterNotifications).forEach(group => {
            group.forEach(master => {
                const opts = master.delivery_options || {};
                settings[master.key] = {
                    enabled: master.is_active,
                    allow_push: !!opts.allow_push,
                    allow_email: !!opts.allow_email,
                    allow_system: !!opts.allow_system,
                    is_obligatory: false,
                };
            });
        });
        return settings;
    };

    const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

    const buildFormSettings = (data) => {
        const defaultSettings = buildDefaultSettings();
        return data && data.id ? { ...defaultSettings, ...data.settings } : defaultSettings;
    };

    const toggleModal = (show, data = {}) => {
        if (show) {
            const next = {
                template_name: data.template_name || '',
                is_default: data.is_default || false,
                settings: buildFormSettings(data),
            };
            initialFormRef.current = JSON.parse(JSON.stringify(next));
            setForm(next);
            setEditData(data);
            setActiveCategory('');
            setSearch('');
        }
        setIsEdit(show);
    };

    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        setSearch('');
    };

    const matchesSearch = (master) => {
        if (!search.trim()) return true;
        const term = search.trim().toLowerCase();
        return formatKey(master.key).toLowerCase().includes(term) || (master.description || '').toLowerCase().includes(term);
    };

    useEffect(() => {
        if (!isEdit) return;
        if (Object.keys(masterNotifications).length === 0) return;
        if (Object.keys(form.settings).length > 0) return;

        const next = { ...form, settings: buildFormSettings(editData) };
        initialFormRef.current = JSON.parse(JSON.stringify(next));
        setForm(next);
    }, [masterNotifications, isEdit]);

    const handleFormChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSettingChange = (key, field, value) => {
        setForm(prev => {
            const keySettings = { ...prev.settings[key], [field]: value };
            if (field === 'enabled' && !value) {
                keySettings.allow_push = false;
                keySettings.allow_email = false;
                keySettings.allow_system = false;
                keySettings.is_obligatory = false;
            }

            if (field === 'allow_push' && value) {
                keySettings.allow_system = true;
            }
            if (field === 'allow_system' && !value) {
                keySettings.allow_push = false;
            }
            return { ...prev, settings: { ...prev.settings, [key]: keySettings } };
        });
    };

    const handleSave = () => {
        if (!form.template_name.trim()) {
            commonStore.addNotification(t('Template name is required'), null, 'error');
            return;
        }
        const existingDefault = templates.find(
            template => template.is_default && template.id !== editData.id
        );
        if (form.is_default && existingDefault) {
            confirmAlert({
                title: t('Replace Company Default Notification Template?'),
                message: t('Template') + ' "' + existingDefault.template_name + '" ' + t('is currently the Company Default notification template. Do you want to replace it with this template?'),
                buttons: [
                    { label: t('Yes'), onClick: performSave },
                    { label: t('No'), onClick: () => { } },
                ],
            });
            return;
        }
        performSave();
    };

    const buildMasterByKey = () => {
        const masterByKey = {};
        Object.values(masterNotifications).forEach((group) => {
            group.forEach((master) => { masterByKey[master.key] = master; });
        });
        return masterByKey;
    };

    const disableSettingsForInactiveMasters = (settings, masterByKey) => {
        const sanitized = {};
        Object.entries(settings).forEach(([key, value]) => {
            const master = masterByKey[key];
            sanitized[key] = (master && !master.is_active)
                ? { ...value, enabled: false, allow_push: false, allow_email: false, allow_system: false, is_obligatory: false }
                : value;
        });
        return sanitized;
    };

    const performSave = async () => {
        setSaving(true);
        try {
            const payload = { ...form, settings: disableSettingsForInactiveMasters(form.settings, buildMasterByKey()) };
            if (editData.id)
                await notificationStore.updateTemplate(editData.id, payload);
            else
                await notificationStore.createTemplate(payload);
            await fetchTemplates();
            setSaving(false);
            setIsEdit(false);
            commonStore.addNotification(t('Saved'), null, 'success');
        } catch (e) {
            setSaving(false);
            commonStore.addNotification(e?.message || t('Something went wrong'), null, 'error');
        }
    };

    const handleDataDelete = (index) => {
        const data = templates[index];
        if (!data || !data.id) return;
        handleDelete(data.id, async (id) => {
            await notificationStore.deleteTemplate(id);
            const res = await fetchTemplates();
            const newPages = (res && res.pages) || 1;
            if (page > newPages - 1) {
                const targetPage = newPages - 1;
                setPage(targetPage);
                await fetchTemplates(targetPage);
            }
            commonStore.addNotification(t('Deleted successfully'), null, 'success');
        });
    };

    const categories = NOTIFICATION_CATEGORIES.filter(
        category => masterNotifications[category] && masterNotifications[category].length > 0
    );

    const formattedTemplates = templates.map(template => ({
        ...template,
        defaultLabel: template.is_default ? t('Yes') : t('No'),
    }));

    if (loading && templates.length === 0) return <LoadingSpinner />;

    return (
        <>
            <Row className="align-items-center">
                <Col xs={8}>
                    <label className="control-label font-size-15 font-color-primary">
                        {t('Notification')} {('Templates')}
                    </label>
                </Col>
                <Col xs={4} className="d-flex justify-content-end">
                    <Button wd fill onClick={() => toggleModal(true, {})}>
                        <i className="fa fa-plus-circle fa-xl margin-right-10" /> {t('Add new')}
                    </Button>
                </Col>
            </Row>
            <Row className="mb-2">
                <Col xs={12}>
                    <p>{t('Note')}** {t('Only one template can be set as Company Default. It applies to all employees with no template assigned. If none is set, master notification settings apply.')}</p>
                </Col>
            </Row>
            <Row className="mb-2 align-items-center">
                <Col md={7}>
                    <div className="d-flex conversation-search">
                        <input
                            type="text"
                            className="conversation-search-input"
                            placeholder={t('Search notification templates')}
                            value={templateSearch}
                            onChange={(e) => setTemplateSearch(e.target.value)}
                        />
                        <i className="conversation-search-icon fa fa-search" />
                    </div>
                </Col>
                <Col md={5} className="d-flex justify-content-end align-items-center gap-2">
                    <SwitchWidget value={defaultOnly} onChange={setDefaultOnly} />
                    <label className="control-label mb-0">{t('Show Company Default Template')}</label>
                </Col>
            </Row>
            <Row>
                <div className="custom-card position-relative">
                    {loading &&
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75 z-1">
                            <LoadingSpinner noMargin />
                        </div>
                    }
                    <TableWidget
                        headers={['Template Name', 'Default']}
                        metaData={[
                            { field: 'template_name', type: 'text', placeholder: 'Template Name' },
                            { field: 'defaultLabel', type: 'text', placeholder: 'Default' },
                        ]}
                        data={formattedTemplates}
                        onChange={() => { }}
                        customDeleteHandler={handleDataDelete}
                        customFormHandler={(idx) => toggleModal(true, templates[idx])}
                    />
                    {formattedTemplates.length === 0 &&
                        <div className='d-flex justify-content-center fw-600 py-3'>{t('No data found')}</div>
                    }
                    {templatesPages > 1 &&
                        <TablePagination
                            pages={templatesPages}
                            page={page}
                            handlePagination={(newPage) => {
                                const targetPage = newPage - 1;
                                setPage(targetPage);
                                fetchTemplates(targetPage);
                            }}
                        />
                    }
                </div>
            </Row>

            <Modal className="modal right fade modal_revised" backdrop="static" show={isEdit} onHide={() => toggleModal(false)}>
                <Modal.Header>
                    <Modal.Title className="w-100 text-center m-0">
                        {editData.id ? (<>{t('Edit')} {t('Template')}</>) : (<>{t('New')} {t('Template')}</>)}
                    </Modal.Title>
                    <img
                        src={CloseModalIcon}
                        className="cursor-pointer"
                        alt="close_button"
                        onClick={() => toggleModal(false)}
                    />
                </Modal.Header>
                <Modal.Body className="position-relative d-flex flex-column p-0">
                    {Object.keys(masterNotifications).length === 0 ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <div className="flex-grow-1 overflow-auto p-3 pb-0">
                                <Row className="mb-3">
                                    <Col md={8}>
                                        <Input
                                            label={t('Template Name')}
                                            value={form.template_name}
                                            placeholder={t('e.g. Standard')}
                                            onChange={e => handleFormChange('template_name', e.target.value)}
                                        />
                                    </Col>
                                    <Col md={4} className="d-flex align-items-center gap-2 pb-1">
                                        <SwitchWidget
                                            value={form.is_default}
                                            onChange={val => handleFormChange('is_default', val)}
                                        />
                                        <label className="control-label mb-0">{t('Company Default Template')}</label>
                                        <OverlayTrigger
                                            placement="left"
                                            overlay={
                                                <Tooltip id="company-default-template-tooltip">
                                                    {t('Only one template can be set as Company Default. It applies to all employees with no template assigned. If none is set, master notification settings apply.')}
                                                </Tooltip>
                                            }
                                        >
                                            <i className="fa fa-info-circle text-muted opacity-50" aria-hidden="true"></i>
                                        </OverlayTrigger>
                                    </Col>
                                </Row>

                                <div className="d-flex conversation-search">
                                    <input
                                        type="text"
                                        className="conversation-search-input"
                                        placeholder={t('Search notifications')}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                    <i className="conversation-search-icon fa fa-search" />
                                </div>

                                <Tabs activeKey={activeCategory || categories[0]} onSelect={handleCategoryChange} id="TemplateNotificationTabs" className="mt-2">
                                    {categories.map(category => (
                                        <Tab key={category} eventKey={category} title={t(NOTIFICATION_CATEGORY_LABELS[category])}>
                                            <Table hover responsive className="mt-3">
                                                <thead>
                                                    <tr>
                                                        <th className="text-start">{t('Notification')}</th>
                                                        <th className="text-center">{t('enabled')}</th>
                                                        <th className="text-center">{t('Push')}</th>
                                                        <th className="text-center">{t('Email')}</th>
                                                        <th className="text-center">{t('System')}</th>
                                                        <th className="text-center">
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id={`obligatory-tooltip-${category}`}>
                                                                        {t("If enabled, this notification will be sent regardless of the user's notification preferences.")}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span>{t('Obligatory')}</span>
                                                            </OverlayTrigger>
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {masterNotifications[category].filter(matchesSearch).map(master => {
                                                        const setting = form.settings[master.key] || { enabled: true, allow_push: true, allow_email: true, allow_system: true, is_obligatory: false };
                                                        const isBatch = !!master.is_batch_notification;
                                                        const masterInactive = !master.is_active;
                                                        const masterDisabled = masterInactive || isBatch;
                                                        const disabledReason = masterInactive
                                                            ? t('This notification is currently inactive and cannot be customized.')
                                                            : isBatch
                                                                ? t('This notification can only be managed by the Super Admin.')
                                                                : null;
                                                        const opts = master.delivery_options || {};
                                                        const allowPush = !!opts.allow_push;
                                                        const allowEmail = !!opts.allow_email;
                                                        const allowSystem = !!opts.allow_system;
                                                        const effectiveSetting = isBatch ? { enabled: master.is_active, allow_push: allowPush, allow_email: allowEmail, allow_system: allowPush || allowSystem, is_obligatory: true } : setting;
                                                        return (
                                                            <tr key={master.key} className={masterDisabled ? 'opacity-50' : ''}>
                                                                <td className="text-start">
                                                                    {disabledReason ? (
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<Tooltip id={`disabled-tooltip-${master.key}`}>{disabledReason}</Tooltip>}
                                                                        >
                                                                            <span>{formatKey(master.key)}</span>
                                                                        </OverlayTrigger>
                                                                    ) : (
                                                                        <span>{formatKey(master.key)}</span>
                                                                    )}
                                                                    {master.description && (
                                                                        <small className="d-block text-muted">{master.description}</small>
                                                                    )}
                                                                </td>
                                                                <td className="text-center">
                                                                    <SwitchWidget
                                                                        value={effectiveSetting.enabled && !masterInactive}
                                                                        onChange={val => handleSettingChange(master.key, 'enabled', val)}
                                                                        disabled={masterDisabled}
                                                                    />
                                                                </td>
                                                                <td className="text-center">
                                                                    {allowPush ? (
                                                                        <SwitchWidget
                                                                            value={effectiveSetting.allow_push && effectiveSetting.enabled && !masterInactive}
                                                                            onChange={val => handleSettingChange(master.key, 'allow_push', val)}
                                                                            disabled={masterDisabled || !setting.enabled}
                                                                        />
                                                                    ) : <span className="text-muted">-</span>}
                                                                </td>
                                                                <td className="text-center">
                                                                    {allowEmail ? (
                                                                        <SwitchWidget
                                                                            value={effectiveSetting.allow_email && effectiveSetting.enabled && !masterInactive}
                                                                            onChange={val => handleSettingChange(master.key, 'allow_email', val)}
                                                                            disabled={masterDisabled || !setting.enabled}
                                                                        />
                                                                    ) : <span className="text-muted">-</span>}
                                                                </td>
                                                                <td className="text-center">
                                                                    {allowSystem ? (
                                                                        <SwitchWidget
                                                                            value={effectiveSetting.allow_system && effectiveSetting.enabled && !masterInactive}
                                                                            onChange={val => handleSettingChange(master.key, 'allow_system', val)}
                                                                            disabled={masterDisabled || !setting.enabled}
                                                                        />
                                                                    ) : <span className="text-muted">-</span>}
                                                                </td>
                                                                <td className="text-center">
                                                                    <SwitchWidget
                                                                        value={!!effectiveSetting.is_obligatory && effectiveSetting.enabled && !masterInactive}
                                                                        onChange={val => handleSettingChange(master.key, 'is_obligatory', val)}
                                                                        disabled={masterDisabled || !setting.enabled}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Tab>
                                    ))}
                                </Tabs>
                            </div>

                            <div className="d-flex justify-content-end gap-2 p-3 border-top bg-white flex-shrink-0">
                                <Button onClick={() => toggleModal(false)} disabled={saving}>
                                    {t('Cancel')}
                                </Button>
                                <Button fill dynamic_lg disabled={saving || !isDirty} onClick={handleSave}>
                                    {saving ? t('Saving...') : <>{editData.id ? t('Update') : t('Create')} {t('Template')}</>}
                                </Button>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default inject('notificationStore', 'commonStore')(withLocalization(withRemoveDialog(observer(NotificationTemplates))));
